import { useMemo } from "react";
import type { Work } from "@/types";
import SectionHeading from "./SectionHeading";
import WorkCard from "./WorkCard";
import { genreTags } from "@/mocks/site";

interface LibrarySectionProps {
  works: Work[];
  categories: string[];
  activeCategory: string;
  onCategoryChange: (value: string) => void;
  activeGenre: string;
  onGenreChange: (value: string) => void;
  keyword: string;
  onKeywordChange: (value: string) => void;
}

export default function LibrarySection({
  works,
  categories,
  activeCategory,
  onCategoryChange,
  activeGenre,
  onGenreChange,
  keyword,
  onKeywordChange,
}: LibrarySectionProps) {
  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return works.filter((work) => {
      const matchCategory = activeCategory === "全部" || work.category === activeCategory;
      const matchGenre = activeGenre === "全部" || work.genres.includes(activeGenre);
      const matchKeyword =
        kw === "" ||
        work.title.toLowerCase().includes(kw) ||
        work.author.toLowerCase().includes(kw) ||
        work.genres.join(" ").toLowerCase().includes(kw);
      return matchCategory && matchGenre && matchKeyword;
    });
  }, [works, activeCategory, activeGenre, keyword]);

  const hasFilter = activeCategory !== "全部" || activeGenre !== "全部" || keyword.trim() !== "";

  const resetAll = () => {
    onCategoryChange("全部");
    onGenreChange("全部");
    onKeywordChange("");
  };

  return (
    <section id="library" className="w-full bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-10">
        <SectionHeading
          eyebrow="作品库 · 分类筛选"
          title="按来源与题材，找到你的下一本"
          desc="切换韩漫 / 国漫 / 动漫分类，叠加题材标签，或直接输入作品名、作者与题材关键词。筛选与搜索只作用于本站已收录的作品，无需跳转。"
        />

        {/* 分类切换 */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          {categories.map((cat) => {
            const active = activeCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => onCategoryChange(cat)}
                className={`whitespace-nowrap rounded-md px-4 h-10 text-sm transition-colors ${
                  active
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-700 border border-background-200/70 hover:border-primary-300"
                }`}
              >
                {cat}
              </button>
            );
          })}
        </div>

        {/* 题材标签 + 搜索 */}
        <div className="mt-4 flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex-1 flex flex-wrap items-center gap-2">
            <span className="text-foreground-500 text-xs md:text-sm mr-1">题材：</span>
            {["全部", ...genreTags].map((g) => {
              const active = activeGenre === g;
              return (
                <button
                  key={g}
                  type="button"
                  onClick={() => onGenreChange(g)}
                  className={`whitespace-nowrap rounded-full px-3 py-1 text-xs md:text-sm transition-colors ${
                    active
                      ? "bg-secondary-500 text-background-50"
                      : "bg-secondary-100 text-secondary-900 hover:bg-secondary-200"
                  }`}
                >
                  {g}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72 shrink-0">
            <i
              className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"
              aria-hidden="true"
            ></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => onKeywordChange(e.target.value)}
              placeholder="搜索作品名 / 作者 / 题材"
              aria-label="搜索作品"
              className="w-full h-11 rounded-md border border-background-200/70 bg-background-50 pl-9 pr-3 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-foreground-500 text-sm">
            共找到 <strong className="text-primary-600">{filtered.length}</strong> 部作品
            {activeCategory !== "全部" && ` · ${activeCategory}`}
            {activeGenre !== "全部" && ` · ${activeGenre}`}
          </p>
          {hasFilter && (
            <button
              type="button"
              onClick={resetAll}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 text-foreground-600 text-sm px-4 h-9 hover:border-primary-300 hover:text-primary-600 transition-colors"
            >
              <i className="ri-restart-line" aria-hidden="true"></i>
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
            {filtered.map((work) => (
              <WorkCard key={work.id} work={work} />
            ))}
          </div>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300/80 bg-background-100/60 py-16 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300" aria-hidden="true"></i>
            <p className="mt-3 text-foreground-600 text-sm md:text-base">没有符合条件的作品，换个筛选条件试试。</p>
            <button
              type="button"
              onClick={resetAll}
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 text-background-50 text-sm px-5 h-10 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-restart-line" aria-hidden="true"></i>
              查看全部作品
            </button>
          </div>
        )}
      </div>
    </section>
  );
}